// biome-ignore lint/style/noRestrictedImports: config
import * as i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
// biome-ignore lint/style/noRestrictedImports: setup i18next
import { initReactI18next } from 'react-i18next';
import * as z from 'zod';

// Adds the type augmentation to i18next
import './types';

import resources from 'virtual:locale-loader';

export type InitLocalizationProps = i18n.InitOptions & {
	module?: i18n.ThirdPartyModule;
	debug?: boolean;
	forceEnglish?: boolean;
};

// TODO: let the user choose a language
// TODO: load the user's selected language
// TODO: Fix locale hmr (see https://github.com/vitejs/vite/blob/734a9e3a4b9a0824a5ba4a5420f9e1176ce74093/packages/vite/src/node/server/hmr.ts#L124)
/**
 * Configures i18next for use with react-i18next.
 *
 * Examples:
 * https://github.com/i18next/react-i18next/tree/master/example/react/public/locales
 * https://github.com/tooot-app/app/blob/main/src/%40types/i18next.d.ts
 * https://github.com/Kaetram/Kaetram-Open/blob/main/packages/common/i18n/options.ts#L185
 */
export async function configLocalization({
	module = initReactI18next,
	debug = false,
	forceEnglish = false,
}: InitLocalizationProps) {
	// bind react-i18next to the i18n instance
	// for all options read: https://www.i18next.com/overview/configuration-options
	const instance = i18n.use(module);

	if (!forceEnglish) {
		// detect user language
		// learn more: https://github.com/i18next/i18next-browser-languageDetector
		instance.use(LanguageDetector);
	}

	// Only load the English locale if forcing English
	const localesToLoad = forceEnglish ? { en: resources.en! } : resources;

	// Initialize i18next
	await instance.init({
		showSupportNotice: false,
		resources: localesToLoad,
		// If forcing English, explicitly set `lng` to "en", otherwise auto-detect
		lng: forceEnglish ? 'en' : undefined,
		fallbackLng: forceEnglish
			? ['en']
			: {
					'zh-Hans': ['zh-CN'],
					'zh-Hant': ['zh-TW'],
					default: ['en'],
				},
		ns: ['common'],
		defaultNS: 'common',
		fallbackNS: ['common'],
		debug,
		interpolation: {
			escapeValue: false, // react is already safe from xss => https://www.i18next.com/translation-function/interpolation#unescape
		},

		// react i18next special options (optional)
		// override if needed - omit if ok with defaults
		react: {
			bindI18n: 'languageChanged',
			bindI18nStore: '',
			transEmptyNodeValue: '',
			transSupportBasicHtmlNodes: true,
			transKeepBasicHtmlNodesFor: ['br', 'strong', 'i'],
			useSuspense: true,
		},
	});

	setZodLocale(forceEnglish ? 'en' : instance.language);
	setGlobalZodErrors(instance.t);

	instance.on('languageChanged', (lng: string) => {
		setZodLocale(lng);
		setGlobalZodErrors(instance.t);
	});

	return instance;
}

function setZodLocale(lang: string) {
	switch (lang) {
		case 'ja':
			z.config(z.locales.ja());
			break;
		case 'zh-CN':
			z.config(z.locales.zhCN());
			break;
		case 'zh-TW':
			z.config(z.locales.zhTW());
			break;
		default:
			z.config(z.locales.en());
			break;
	}
}

function setGlobalZodErrors(t: i18n.TFunction) {
	z.config({
		customError: iss => {
			if (iss.code === 'too_small' && iss.minimum === 1) {
				if (iss.origin === 'array') {
					return t('zod:errors.too_small.array.inclusive');
				} else {
					return t('zod:errors.too_small.string.inclusive');
				}
			}

			if (iss.code === 'invalid_format' && iss.format === 'email') {
				return t('zod:errors.LOC_ValidationMessage_InvalidEmail');
			}
		},
	});
}
